import type {
  LockerverseSdkError,
  LockerverseSignupClient,
  LockerverseSignup as LockerverseSignupDefinition,
  LockerverseSignupSubmissionResult,
} from "@lockerverse/sdk/signup";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { getAccentContrast, normalizeAccentColor } from "./accent-contrast.ts";
import { getPhoneServerError } from "./contact-validation.ts";
import {
  buildCustomFieldAnswers,
  CustomFields,
  createCustomFieldValues,
  reconcileCustomFieldValues,
} from "./custom-fields.tsx";
import { EmailField, FieldError, FormField } from "./form-field.tsx";
import { FormSkeleton } from "./form-skeleton.tsx";
import { notifyHostCallback } from "./host-callback.ts";
import { getInternationalPhone } from "./phone.ts";
import { PhoneInput } from "./phone-field.tsx";
import { SignupAddressField } from "./signup-address-field.tsx";
import type { LockerverseSignupProps } from "./signup-types.ts";
import {
  type SignupFormValues as FormValues,
  isAddressEmpty,
  type SignupAddressValues,
  validateSignupForm,
} from "./signup-validation.ts";

const DEFAULT_ADDRESS: SignupAddressValues = {
  city: "",
  country: "US",
  line1: "",
  line2: "",
  postalCode: "",
  state: "",
};

const DEFAULT_ACCENT = "#5751f2";

function createInitialValues(signup: LockerverseSignupDefinition): FormValues {
  return {
    address: DEFAULT_ADDRESS,
    customFields: createCustomFieldValues(signup.customFields),
    email: "",
    honeypot: "",
    name: "",
    participantCount: "",
    phone: "",
    phoneCountry: "US",
  };
}

function focusNamedControl(form: HTMLFormElement, name: string) {
  const control = form.elements.namedItem(name);
  if (control instanceof HTMLElement) {
    if (!control.matches(":disabled")) {
      control.focus();
      return true;
    }
    return false;
  }
  if (control instanceof RadioNodeList) {
    const focusable = Array.from(control).find(
      (item) => item instanceof HTMLElement && !item.matches(":disabled")
    );
    focusable?.focus();
    return Boolean(focusable);
  }
  return false;
}

function focusFirstSignupValidationError(
  form: HTMLFormElement,
  errors: Record<string, string>
) {
  const builtInFields: [string, string][] = [
    ["name", "name"],
    ["email", "email"],
    ["phone", "phone"],
    ["participantCount", "participantCount"],
    ["address", "address.line1"],
  ];
  for (const [errorKey, controlName] of builtInFields) {
    if (errors[errorKey] && focusNamedControl(form, controlName)) {
      return;
    }
  }
  const builtInKeys = new Set(builtInFields.map(([errorKey]) => errorKey));
  const customFieldId = Object.keys(errors).find(
    (errorKey) => !builtInKeys.has(errorKey)
  );
  if (customFieldId) {
    focusNamedControl(form, `custom:${customFieldId}`);
  }
}

type SignupViewProps = Omit<LockerverseSignupProps, "widget">;

function SignupShell({
  children,
  loading = false,
  status = false,
  viewProps,
}: {
  children: ReactNode;
  loading?: boolean;
  status?: boolean;
  viewProps: SignupViewProps;
}) {
  const {
    branding,
    className,
    googlePlacesApiKey: _googlePlacesApiKey,
    onSignupComplete: _onSignupComplete,
    style,
    theme = "dark",
    ...sectionProps
  } = viewProps;
  const accent = normalizeAccentColor(
    style?.["--lockerverse-accent"] ??
      style?.["--lockerverse-signup-accent"] ??
      branding?.accentColor ??
      DEFAULT_ACCENT
  );
  const signupStyle: LockerverseSignupProps["style"] = { ...style };
  if (
    style?.["--lockerverse-accent"] !== undefined ||
    style?.["--lockerverse-signup-accent"] !== undefined ||
    branding?.accentColor
  ) {
    signupStyle["--lockerverse-accent"] = accent;
  }
  if (
    style?.["--lockerverse-accent-contrast"] === undefined &&
    style?.["--lockerverse-signup-accent-contrast"] === undefined
  ) {
    const accentContrast = getAccentContrast(accent);
    if (accentContrast) {
      signupStyle["--lockerverse-accent-contrast"] = accentContrast;
    }
  }
  return (
    <section
      {...sectionProps}
      {...(loading ? { "aria-busy": true } : {})}
      className={[
        "lockerverse-signup",
        status ? "lockerverse-signup__status" : null,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-theme={theme}
      style={signupStyle}
    >
      {children}
    </section>
  );
}

type SignupSubmissionState =
  | { phase: "idle" }
  | { phase: "submitting" }
  | {
      error: LockerverseSdkError;
      phase: "submit_error";
    }
  | {
      phase: "submitted";
    };

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: The component renders server-defined field branches in one public form boundary.
function SignupForm({
  client,
  loadError,
  onRetry,
  signup,
  ...viewProps
}: SignupViewProps & {
  client: LockerverseSignupClient;
  loadError?: LockerverseSdkError | null;
  onRetry?: () => void;
  signup: LockerverseSignupDefinition;
}) {
  const { branding, googlePlacesApiKey, onSignupComplete } = viewProps;
  const idPrefix = useId();
  const mountedRef = useRef(true);
  const phoneRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);
  const [state, setState] = useState<SignupSubmissionState>({ phase: "idle" });
  const [values, setValues] = useState<FormValues>(() =>
    createInitialValues(signup)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submissionRetry, setSubmissionRetry] = useState<{
    payloadKey: string;
    reference: string;
  }>();
  const customFieldValues = reconcileCustomFieldValues(
    signup.customFields,
    values.customFields
  );

  useEffect(() => {
    if (customFieldValues !== values.customFields) {
      setValues((current) => ({
        ...current,
        customFields: reconcileCustomFieldValues(
          signup.customFields,
          current.customFields
        ),
      }));
    }
  }, [customFieldValues, signup.customFields, values.customFields]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (state.phase !== "submitting" && errors.phone) {
      phoneRef.current?.focus();
    }
  }, [errors.phone, state.phase]);

  if (!signup.isActive) {
    return (
      <SignupShell status viewProps={viewProps}>
        <h1>Signup closed</h1>
        <p>This signup is not accepting submissions.</p>
      </SignupShell>
    );
  }

  if (state.phase === "submitted") {
    return (
      <SignupShell status viewProps={viewProps}>
        {branding ? <Branding branding={branding} /> : null}
        <span aria-hidden="true" className="lockerverse-signup__success">
          ✓
        </span>
        <h1>Thank you</h1>
        <p>Your signup has been received.</p>
      </SignupShell>
    );
  }

  const update = (patch: Partial<FormValues>) =>
    setValues((current) => ({ ...current, ...patch }));
  const submitting = state.phase === "submitting";
  const submitError =
    state.phase === "submit_error" ? state.error.message : undefined;
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Submission maps the enabled server-defined fields to one SDK request.
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // biome-ignore lint/suspicious/noUnnecessaryConditions: The ref blocks a second submit before React commits the submitting state.
    if (submittingRef.current) {
      return;
    }
    const validation = validateSignupForm(signup, {
      ...values,
      customFields: customFieldValues,
      phone: signup.fields.phone.enabled
        ? getInternationalPhone(values.phone, values.phoneCountry)
        : "",
    });
    setErrors(validation.errors);
    if (Object.keys(validation.errors).length > 0 || !validation.value) {
      const form = event.currentTarget;
      queueMicrotask(() =>
        focusFirstSignupValidationError(form, validation.errors)
      );
      return;
    }
    const validatedValues = validation.value;
    const submission = {
      address:
        signup.fields.address.enabled &&
        !isAddressEmpty(validatedValues.address)
          ? validatedValues.address
          : null,
      answers: buildCustomFieldAnswers(
        signup.customFields,
        validatedValues.customFields
      ),
      email: signup.fields.email.enabled ? validatedValues.email || null : null,
      honeypot: validatedValues.honeypot,
      name: validatedValues.name,
      participantCount: signup.fields.participantCount.enabled
        ? validatedValues.participantCount
        : null,
      phone: signup.fields.phone.enabled ? validatedValues.phone || null : null,
    };
    const payloadKey = JSON.stringify(submission);
    const submissionReference =
      submissionRetry?.payloadKey === payloadKey
        ? submissionRetry.reference
        : globalThis.crypto.randomUUID();
    let result: LockerverseSignupSubmissionResult;
    submittingRef.current = true;
    setState({ phase: "submitting" });
    try {
      result = await client.submit(submission, { submissionReference });
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      // SAFETY: LockerverseSignupClient normalizes each submission rejection to LockerverseSdkError.
      const sdkError = error as LockerverseSdkError;
      const phoneError = getPhoneServerError(sdkError.serverCode);
      if (phoneError) {
        setErrors((current) => ({ ...current, phone: phoneError }));
        setState({ phase: "idle" });
        setSubmissionRetry(undefined);
        return;
      }
      setState({ error: sdkError, phase: "submit_error" });
      if (sdkError.recoveryRecommended) {
        setSubmissionRetry({
          payloadKey,
          reference: submissionReference,
        });
      } else {
        setSubmissionRetry(undefined);
      }
      return;
    } finally {
      submittingRef.current = false;
    }
    // biome-ignore lint/suspicious/noUnnecessaryConditions: The ref changes during unmount while this request is pending.
    if (!mountedRef.current) {
      return;
    }
    setSubmissionRetry(undefined);
    setState({ phase: "submitted" });
    notifyHostCallback(onSignupComplete, result);
  };

  return (
    <SignupShell viewProps={viewProps}>
      {signup.coverImageUrl ? (
        <img
          alt=""
          className="lockerverse-signup__cover"
          height={192}
          src={signup.coverImageUrl}
          width={440}
        />
      ) : null}
      {branding ? <Branding branding={branding} /> : null}
      <header>
        {signup.title?.trim() ? <h1>{signup.title}</h1> : null}
        {signup.description ? <p>{signup.description}</p> : null}
      </header>
      {loadError ? (
        <div role="alert">
          <p>{loadError.message}</p>
          <button onClick={onRetry} type="button">
            Retry
          </button>
        </div>
      ) : null}
      <form noValidate onSubmit={handleSubmit}>
        <input
          aria-hidden="true"
          autoComplete="off"
          className="lockerverse-signup__honeypot"
          onChange={(event) => update({ honeypot: event.currentTarget.value })}
          tabIndex={-1}
          value={values.honeypot}
        />
        <div className="lockerverse-signup__fields">
          <label htmlFor={`${idPrefix}-name`}>
            <span>Name</span>
            <input
              aria-describedby={
                errors.name ? `${idPrefix}-name-error` : undefined
              }
              aria-invalid={errors.name ? "true" : undefined}
              autoComplete="name"
              id={`${idPrefix}-name`}
              name="name"
              onChange={(event) => update({ name: event.currentTarget.value })}
              required
              value={values.name}
            />
            <FieldError error={errors.name} id={`${idPrefix}-name-error`} />
          </label>
          {signup.fields.email.enabled ? (
            <EmailField
              error={errors.email}
              id={`${idPrefix}-email`}
              label="Email"
              name="email"
              onChange={(event) => update({ email: event.currentTarget.value })}
              required={signup.fields.email.required}
              value={values.email}
            />
          ) : null}
          {signup.fields.phone.enabled ? (
            <FormField
              error={errors.phone}
              id={`${idPrefix}-phone`}
              label="Phone"
            >
              <PhoneInput
                country={values.phoneCountry}
                error={errors.phone}
                errorId={`${idPrefix}-phone-error`}
                id={`${idPrefix}-phone`}
                inputRef={phoneRef}
                name="phone"
                onChange={({ value, country }) =>
                  update({ phone: value, phoneCountry: country })
                }
                required={signup.fields.phone.required}
                value={values.phone}
              />
            </FormField>
          ) : null}
          {signup.fields.participantCount.enabled ? (
            <label htmlFor={`${idPrefix}-participant-count`}>
              <span>Guest count</span>
              <input
                aria-describedby={
                  errors.participantCount
                    ? `${idPrefix}-participant-count-error`
                    : undefined
                }
                aria-invalid={errors.participantCount ? "true" : undefined}
                id={`${idPrefix}-participant-count`}
                min={1}
                name="participantCount"
                onChange={(event) =>
                  update({ participantCount: event.currentTarget.value })
                }
                required={signup.fields.participantCount.required}
                type="number"
                value={values.participantCount}
              />
              <FieldError
                error={errors.participantCount}
                id={`${idPrefix}-participant-count-error`}
              />
            </label>
          ) : null}
          {signup.fields.address.enabled ? (
            <div className="lockerverse-signup__field-group">
              <SignupAddressField
                apiKey={googlePlacesApiKey}
                errorId={
                  errors.address ? `${idPrefix}-address-error` : undefined
                }
                invalid={Boolean(errors.address)}
                onChange={(address) => update({ address })}
                required={signup.fields.address.required}
                value={values.address}
              />
              <FieldError
                error={errors.address}
                id={`${idPrefix}-address-error`}
              />
            </div>
          ) : null}
          <CustomFields
            choiceClassName="lockerverse-signup__choice"
            errorClassName="lockerverse-signup__field-error"
            errors={errors}
            fields={signup.customFields}
            idPrefix={idPrefix}
            onChange={(customFields) => update({ customFields })}
            values={customFieldValues}
          />
        </div>
        {submitError ? (
          <p className="lockerverse-signup__submit-error" role="alert">
            {submitError} Try again.
          </p>
        ) : null}
        <button
          className="lockerverse-signup__submit"
          disabled={submitting}
          type="submit"
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </form>
    </SignupShell>
  );
}

export function LockerverseSignup({
  widget,
  ...viewProps
}: LockerverseSignupProps) {
  if (widget.error && !widget.signup) {
    return (
      <SignupShell status viewProps={viewProps}>
        <h1>Signup unavailable</h1>
        <p role="alert">{widget.error.message}</p>
        <button onClick={widget.refresh} type="button">
          Retry
        </button>
      </SignupShell>
    );
  }
  if (!widget.signup) {
    return (
      <SignupShell loading viewProps={viewProps}>
        <FormSkeleton fieldCount={4} label="Loading signup…" />
      </SignupShell>
    );
  }
  return (
    <SignupForm
      client={widget.client}
      loadError={widget.error}
      onRetry={widget.refresh}
      signup={widget.signup}
      {...viewProps}
    />
  );
}

function Branding({
  branding,
}: {
  branding: NonNullable<LockerverseSignupProps["branding"]>;
}) {
  return (
    <div className="lockerverse-signup__branding">
      {branding.imageUrl ? (
        <img alt="" height={48} src={branding.imageUrl} width={48} />
      ) : null}
      <strong>{branding.name}</strong>
    </div>
  );
}
